/**
 * App root: providers + hash router + cross-cutting lifecycle wiring.
 *
 * Routing map (feature #1):
 *   /                 SplashScreen (auth check → redirect)
 *   /login            LoginScreen (phone number + OTP)
 *   /register         RegisterScreen (post-OTP, signed-in without profile only)
 *   /under-review     UnderReviewScreen
 *   /blocked          BlockedScreen
 *   /profile          ProfileScreen           (approved-only)
 *   /saved            SavedScreen (wishlist)  (approved-only)
 *   /scratch          ScratchCardScreen       (approved-only; admin toggle)
 *   /home             HomeScreen              (approved-only)
 *   /category/:name   HomeScreen (tab preselected, deep-link)   (approved-only)
 *   /product/:id      ProductDetailScreen (deep-link)           (approved-only)
 *
 * A HashRouter is used because Capacitor loads index.html from the file/
 * capacitor scheme where path-based routing + refresh is unreliable; hash
 * routing "just works" in the WebView and on static hosting.
 */
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './features/auth/AuthContext';
import { ToastProvider } from './components/Toast';
import { ForceUpdateGate, ApprovedRoute, RegistrationRoute } from './routes/Guards';
import { ApkUpdateGate } from './components/UpdateBanner';
import { DemoBanner } from './components/DemoBanner';
import { AppLifecycle } from './AppLifecycle';
import { ROUTES } from './routes/paths';
import { ProductOverlay, ProductOverlayProvider, backgroundOf } from './routes/productOverlay';

import { SplashScreen } from './features/auth/SplashScreen';
import { LoginScreen } from './features/auth/LoginScreen';
import { RegisterScreen } from './features/registration/RegisterScreen';
import { UnderReviewScreen } from './features/auth/UnderReviewScreen';
import { BlockedScreen } from './features/auth/BlockedScreen';
import { HomeScreen } from './features/home/HomeScreen';
import { ProductDetailScreen } from './features/product/ProductDetailScreen';
import { ProfileScreen } from './features/profile/ProfileScreen';
import { SavedScreen } from './features/saved/SavedScreen';
import { ScratchCardScreen } from './features/scratch/ScratchCardScreen';

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <AuthProvider>
          <ToastProvider>
            <ForceUpdateGate>
              <ApkUpdateGate>
                <AppLifecycle />
                <AppRoutes />
              </ApkUpdateGate>
            </ForceUpdateGate>
          </ToastProvider>
        </AuthProvider>
      </HashRouter>
    </QueryClientProvider>
  );
}

/**
 * The screens. A product opened from a list carries `state.background` (see
 * routes/productOverlay.tsx): the routes then render that list's location and
 * the product page is drawn over it, so going back — Android back included —
 * returns to the list exactly as it was. Anything else renders normally.
 */
function AppRoutes(): JSX.Element {
  const location = useLocation();
  const background = backgroundOf(location);
  return (
    <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col bg-slate-50">
      <DemoBanner />
      <ProductOverlayProvider open={background !== undefined}>
        <Routes location={background ?? location}>
          <Route path={ROUTES.splash} element={<SplashScreen />} />
          <Route path={ROUTES.login} element={<LoginScreen />} />
          <Route
            path={ROUTES.register}
            element={
              <RegistrationRoute>
                <RegisterScreen />
              </RegistrationRoute>
            }
          />
          <Route path={ROUTES.underReview} element={<UnderReviewScreen />} />
          <Route path={ROUTES.blocked} element={<BlockedScreen />} />

          <Route
            path={ROUTES.home}
            element={
              <ApprovedRoute>
                <HomeScreen />
              </ApprovedRoute>
            }
          />
          <Route
            path={ROUTES.categoryPattern}
            element={
              <ApprovedRoute>
                <HomeScreen />
              </ApprovedRoute>
            }
          />
          <Route
            path={ROUTES.productPattern}
            element={
              <ApprovedRoute>
                <ProductDetailScreen />
              </ApprovedRoute>
            }
          />
          <Route
            path={ROUTES.profile}
            element={
              <ApprovedRoute>
                <ProfileScreen />
              </ApprovedRoute>
            }
          />
          <Route
            path={ROUTES.saved}
            element={
              <ApprovedRoute>
                <SavedScreen />
              </ApprovedRoute>
            }
          />
          <Route
            path={ROUTES.scratch}
            element={
              <ApprovedRoute>
                <ScratchCardScreen />
              </ApprovedRoute>
            }
          />

          <Route path="*" element={<Navigate to={ROUTES.splash} replace />} />
        </Routes>
      </ProductOverlayProvider>
      {background ? (
        <Routes>
          <Route
            path={ROUTES.productPattern}
            element={
              <ApprovedRoute>
                <ProductOverlay>
                  <ProductDetailScreen />
                </ProductOverlay>
              </ApprovedRoute>
            }
          />
        </Routes>
      ) : null}
    </div>
  );
}
